import { ContentfulClientApi } from "contentful";
import { cacheTimeoutMilliseconds, contentfulClient } from "./contentful-client";
import { ContentType } from "./types/content-type";
import { LanguageResolver } from "../i18n/language-resolver";
import memoryCache from "memory-cache";
import { TypeStickyVinSearchSkeleton } from "./types/generated";
import { ModuleStickyVinSearch } from "./PageModularModule";
import { TModuleStickyVinSearchFields } from "./types/wrappers";

interface StickyVinSearchEntry {
  blogStickyVinSearch: ModuleStickyVinSearch | null;
  modularPageStickyVinSearch: ModuleStickyVinSearch | null;
  excludedPaths: string[];
}

export default class StickyVinSearchApi {
  private static instance: StickyVinSearchApi;
  private readonly client: ContentfulClientApi<undefined>;

  private constructor() {
    this.client = contentfulClient.getInstance();
  }

  public static getInstance(): StickyVinSearchApi {
    if (StickyVinSearchApi.instance) {
      return StickyVinSearchApi.instance;
    }

    StickyVinSearchApi.instance = new StickyVinSearchApi();
    return StickyVinSearchApi.instance;
  }

  public async getBlogStickyVinSearch(locale: string, path?: string): Promise<ModuleStickyVinSearch | null> {
    const entry = await this.getEntry(locale);
    if (!entry) return null;
    if (path && this.isPathExcluded(path, entry.excludedPaths)) return null;
    return entry.blogStickyVinSearch;
  }

  public async getModularPageStickyVinSearch(locale: string, path?: string): Promise<ModuleStickyVinSearch | null> {
    const entry = await this.getEntry(locale);
    if (!entry) return null;
    if (path && this.isPathExcluded(path, entry.excludedPaths)) return null;
    return entry.modularPageStickyVinSearch;
  }

  private isPathExcluded(path: string, excludedPaths: string[]): boolean {
    const normalizedPath = path.replace(/\/$/, "") || "/";
    return excludedPaths.some((excluded) => {
      const normalizedExcluded = excluded.replace(/\/$/, "") || "/";
      return normalizedPath === normalizedExcluded;
    });
  }

  private async getEntry(locale: string): Promise<StickyVinSearchEntry | null> {
    const cacheKey = `sticky-vin-search-${locale}`;
    const cached = memoryCache.get(cacheKey);
    if (cached !== null) return cached;

    try {
      const entries = await this.client.withoutUnresolvableLinks.getEntries<TypeStickyVinSearchSkeleton>({
        content_type: ContentType.StickyVinSearch,
        locale: locale || LanguageResolver.getCurrentLocale(),
        limit: 1,
        include: 2,
      });
      const fields = entries.items[0]?.fields;
      if (!fields) {
        memoryCache.put(cacheKey, null, cacheTimeoutMilliseconds);
        return null;
      }

      const blogFields = fields.blogStickyVinSearch?.fields as TModuleStickyVinSearchFields;
      const modularFields = fields.modularPageStickyVinSearch?.fields as TModuleStickyVinSearchFields;

      const result: StickyVinSearchEntry = {
        blogStickyVinSearch: blogFields
          ? {
              heading: blogFields.heading ?? "",
              buttonText: blogFields.buttonText ?? "",
              customLabel: blogFields.customLabel ?? "",
              customPlaceholder: blogFields.customPlaceholder ?? "",
              customVinsearchButton: blogFields.customVinsearchButton ?? "",
            }
          : null,
        modularPageStickyVinSearch: modularFields
          ? {
              heading: modularFields.heading ?? "",
              buttonText: modularFields.buttonText ?? "",
              customLabel: modularFields.customLabel ?? "",
              customPlaceholder: modularFields.customPlaceholder ?? "",
              customVinsearchButton: modularFields.customVinsearchButton ?? "",
            }
          : null,
        excludedPaths: (fields.excludedPaths as string[]) ?? [],
      };

      memoryCache.put(cacheKey, result, cacheTimeoutMilliseconds);
      return result;
    } catch {
      return null;
    }
  }
}
